import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Users, UserCheck, UserX, Clock, Wallet, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrency } from '../hooks/useCurrency';
import { StatCard } from '../components/dashboard/StatCard';
import { QuickActions } from '../components/dashboard/QuickActions';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { format: fmt } = useCurrency();

  const fetchStats = useCallback(async () => {
    try {
      const localDate = format(new Date(), 'yyyy-MM-dd');
      const response = await axios.get(`${API_URL}/api/dashboard/stats`, { params: { date: localDate } });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2E4632]"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Workers',
      value: stats?.total_workers || 0,
      icon: Users,
      color: 'bg-[#2E4632]',
      textColor: 'text-white',
      testId: 'stat-total-workers'
    },
    {
      title: 'Present Today',
      value: (stats?.present_today ?? 0).toFixed(1),
      icon: UserCheck,
      color: 'bg-white border border-[#D1D1CD]',
      textColor: 'text-[#2E4632]',
      iconColor: 'text-[#2E4632]',
      testId: 'stat-present-today'
    },
    {
      title: 'Absent Today',
      value: (stats?.absent_today ?? 0).toFixed(1),
      icon: UserX,
      color: 'bg-white border border-[#D1D1CD]',
      textColor: 'text-[#BC4749]',
      iconColor: 'text-[#BC4749]',
      testId: 'stat-absent-today'
    },
    {
      title: 'Pending Leaves',
      value: stats?.pending_leaves || 0,
      icon: Clock,
      color: 'bg-[#D97706]',
      textColor: 'text-white',
      testId: 'stat-pending-leaves'
    },
    {
      title: 'Pending Advances',
      value: fmt(stats?.total_pending_advances || 0),
      icon: Wallet,
      color: 'bg-white border border-[#D1D1CD]',
      textColor: 'text-[#D97706]',
      iconColor: 'text-[#D97706]',
      testId: 'stat-pending-advances'
    },
    {
      title: 'This Month Wages',
      value: fmt(stats?.month_wages || 0),
      icon: TrendingUp,
      color: 'bg-[#2E4632]',
      textColor: 'text-white',
      testId: 'stat-month-wages'
    }
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className="text-3xl md:text-4xl font-bold text-[#1C1C1E] tracking-tight"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Dashboard
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            Today: {stats?.today || format(new Date(), 'yyyy-MM-dd')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6">
        {statCards.map((stat, index) => (
          <StatCard key={stat.title} stat={stat} index={index} />
        ))}
      </div>

      <QuickActions />
    </div>
  );
};

export default Dashboard;
